import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Loader2, MapPin, Briefcase } from "lucide-react";
import { API, AuthContext } from "../../App";
import { toast } from "sonner";
import JobFormDialog from "../../components/jobs/JobFormDialog";
import StoreJobFormDialog from "../../components/jobs/StoreJobFormDialog";
import { useTimezone } from "../../contexts/TimezoneContext";

/**
 * Full-page job create/edit — replaces the old modal flow so nested
 * dropdowns never fight the dialog focus trap (crash fix).
 * Routes: /dashboard/jobs/new  ·  /dashboard/jobs/:jobId/edit
 * Prefill (Paste Ticket / New Job for Customer) arrives via location.state.
 */
export default function JobFormPage() {
  const { user } = useContext(AuthContext);
  const isStoreAccount = user?.account_type === "multi_location_store";
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getToday } = useTimezone();
  const ticketPrefill = location.state?.ticketPrefill || null;
  const customerPrefill = location.state?.customerPrefill || null;
  const schedulePrefill = location.state?.schedulePrefill || null;
  // Stable initial date — a fresh Date each render would churn child effects.
  const [initialDate] = useState(() => getToday());

  const [customers, setCustomers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [jobSources, setJobSources] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [vehicleData, setVehicleData] = useState({ makes: [], models_by_make: {}, years: [] });
  const [loading, setLoading] = useState(true);
  const [editingJob, setEditingJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(!!jobId);

  const fetchReferenceData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [customersRes, techniciansRes, jobTypesRes, sourcesRes, paymentsRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/customers`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/technicians`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/job-types`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/job-sources`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/payment-types`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/vehicles/all`, config).catch(() => ({ data: { makes: [], models_by_make: {}, years: [] } })),
      ]);
      const techniciansData = Array.isArray(techniciansRes.data) ? techniciansRes.data : [];
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
      setTeamMembers(techniciansData.map(t => ({ ...t, member_id: t.technician_id, status: t.status || "active" })));
      setJobTypes(Array.isArray(jobTypesRes.data) ? jobTypesRes.data : []);
      setJobSources(Array.isArray(sourcesRes.data) ? sourcesRes.data : []);
      setPaymentTypes(Array.isArray(paymentsRes.data) ? paymentsRes.data : []);
      setVehicleData(vehiclesRes.data || { makes: [], models_by_make: {}, years: [] });
    } catch {
      toast.error("Failed to load form data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReferenceData(); }, [fetchReferenceData]);

  // Edit mode — fetch the job directly so deep links work from anywhere.
  useEffect(() => {
    if (!jobId) { setEditingJob(null); setJobLoading(false); return; }
    const token = localStorage.getItem("token");
    setJobLoading(true);
    axios.get(`${API}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setEditingJob(res.data))
      .catch(() => {
        toast.error("Failed to load job");
        navigate("/dashboard/jobs");
      })
      .finally(() => setJobLoading(false));
  }, [jobId, navigate]);

  const returnTo = location.state?.returnTo || "/dashboard/jobs";
  const goBack = useCallback(() => navigate(returnTo), [navigate, returnTo]);
  const handleClose = useCallback((isOpen) => { if (!isOpen) goBack(); }, [goBack]);

  const title = jobId
    ? (isStoreAccount ? "Edit Store Job" : "Edit Job")
    : (isStoreAccount ? "New Store Job" : "Create New Job");

  return (
    <div className="max-w-3xl mx-auto pb-4" data-testid="job-form-page">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-5">
        <Button
          variant="ghost" size="icon"
          onClick={goBack}
          className="h-9 w-9 text-muted-foreground hover:text-foreground hover:bg-theme-hover shrink-0"
          data-testid="job-form-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          {isStoreAccount
            ? <MapPin className="w-5 h-5 text-blue-500" />
            : <Briefcase className="w-5 h-5 text-[#FFCC00]" />}
          <h2 className="text-xl sm:text-2xl font-bold text-foreground font-['Manrope']" data-testid="job-form-page-title">
            {title}
          </h2>
        </div>
      </div>

      {(jobLoading || loading) ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="job-form-page-loading">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> {jobId ? "Loading job…" : "Loading…"}
        </div>
      ) : isStoreAccount ? (
        <StoreJobFormDialog
          variant="page"
          open={true}
          onOpenChange={handleClose}
          editingJob={editingJob}
          ticketPrefill={ticketPrefill}
          customerPrefill={customerPrefill}
          schedulePrefill={schedulePrefill}
          customers={customers}
          teamMembers={teamMembers}
          vehicleData={vehicleData}
          onSuccess={goBack}
        />
      ) : (
        <JobFormDialog
          variant="page"
          open={true}
          onOpenChange={handleClose}
          editingJob={editingJob}
          ticketPrefill={ticketPrefill}
          customerPrefill={customerPrefill}
          schedulePrefill={schedulePrefill}
          selectedDate={initialDate}
          customers={customers}
          teamMembers={teamMembers}
          jobTypes={jobTypes}
          jobSources={jobSources}
          paymentTypes={paymentTypes}
          vehicleData={vehicleData}
          loading={loading}
          onSuccess={goBack}
          onRefresh={fetchReferenceData}
        />
      )}
    </div>
  );
}
